import { CopilotDataPartEnum, CopilotResourceKindEnum, CopilotRoleEnum } from "~/utils/enums/copilot/chat.enums";
import { CopilotErrorCodeEnum, CopilotErrorRecoveryEnum } from "~/utils/enums/copilot/errors.enums";
import type { ICopilotError } from "~/utils/interfaces/copilot-blocks-interfaces";
import type { CopilotResource } from "~/utils/interfaces/copilot-interfaces";
import { copilotResourceHref } from "~/utils/helpers/copilot/resources";

const ACCEPTED_FILE_MEDIA_TYPE = "application/pdf";
const MAX_FILE_BYTES = 25 * 1024 * 1024;
const MAX_MANUAL_RETRIES = 3;
const PROGRAM_URL_PROMPT_PREFIX = "Importe ce programme de formation : ";
const DATA_ERROR_PART_TYPE = `data-${CopilotDataPartEnum.ERROR}`;

const ERROR_CODES = new Set<string>(Object.values(CopilotErrorCodeEnum));
const ERROR_RECOVERIES = new Set<string>(Object.values(CopilotErrorRecoveryEnum));

const ERROR_MESSAGES: Record<CopilotErrorCodeEnum, string> = {
  [CopilotErrorCodeEnum.MULTI_TIMESLOT_SESSION]:
    "Simbel AI cannot process attendance sheets for sessions with multiple steps. Enter attendance from the session page.",
  [CopilotErrorCodeEnum.INVALID_URL]: "Ce lien est invalide ou ne peut pas être ouvert.",
  [CopilotErrorCodeEnum.DOCUMENT_REQUIRED]: "Ajoutez un programme au format PDF pour continuer.",
  [CopilotErrorCodeEnum.SOURCE_UNREADABLE]: "Le contenu n’a pas pu être lu. Essayez avec un autre PDF.",
  [CopilotErrorCodeEnum.NOT_FOUND]: "La formation demandée est introuvable.",
  // Names a session and asks for one: the sheet is already read. Why nothing matched is never said.
  [CopilotErrorCodeEnum.SESSION_NOT_FOUND]:
    "Aucune session correspondante n’a été trouvée. Indiquez la session concernée (nom, date ou code).",
  [CopilotErrorCodeEnum.FORBIDDEN]: "Droits insuffisants pour cette action.",
  [CopilotErrorCodeEnum.SESSION_EXPIRED]: "La session a expiré. Reconnexion nécessaire pour continuer.",
  [CopilotErrorCodeEnum.DOCUMENT_TOO_LARGE]: `Ce PDF dépasse ${MAX_FILE_BYTES / 1024 / 1024} Mo. Essayez avec un fichier plus léger.`,
  [CopilotErrorCodeEnum.RESPONSE_INTERRUPTED]: "La réponse s’est interrompue.",
  [CopilotErrorCodeEnum.TEMPORARY_FAILURE]: "Le service est temporairement indisponible.",
  [CopilotErrorCodeEnum.OPERATION_FAILED]: "L’opération n’a pas pu aboutir.",
};

/** Codes whose CORRECT_INPUT carries no link to reopen: the message alone, never a dead button. */
const LINKLESS_CORRECTIONS = new Set<CopilotErrorCodeEnum>([
  CopilotErrorCodeEnum.NOT_FOUND,
  CopilotErrorCodeEnum.SESSION_NOT_FOUND,
]);

const OPERATION_FAILED_ERROR: ICopilotError = {
  code: CopilotErrorCodeEnum.OPERATION_FAILED,
  recovery: CopilotErrorRecoveryEnum.NONE,
};

export class CopilotHttpError extends Error {
  constructor(
    readonly status: number,
    readonly payload?: unknown,
  ) {
    super(`Document assistant request failed with status ${status}`);
    this.name = "CopilotHttpError";
  }
}

type CopilotFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export function createCopilotFetch(fetchImplementation: CopilotFetch = globalThis.fetch) {
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await fetchImplementation(input, init);
    if (response.ok) return response;

    let payload: unknown;
    try {
      payload = await response.clone().json();
    } catch {
      payload = undefined;
    }

    throw new CopilotHttpError(response.status, payload);
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isDocumentAssistantError(value: unknown): value is ICopilotError {
  return (
    isRecord(value) &&
    typeof value.code === "string" &&
    ERROR_CODES.has(value.code) &&
    typeof value.recovery === "string" &&
    ERROR_RECOVERIES.has(value.recovery)
  );
}

function structuredErrorOf(value: unknown): ICopilotError | null {
  if (isRecord(value) && value.type === DATA_ERROR_PART_TYPE) return structuredErrorOf(value.data);
  if (isRecord(value) && value.code === CopilotErrorCodeEnum.MULTI_TIMESLOT_SESSION) {
    const resource = value.resource;

    return {
      code: CopilotErrorCodeEnum.MULTI_TIMESLOT_SESSION,
      recovery: CopilotErrorRecoveryEnum.NONE,
      ...(typeof value.message === "string" && value.message.trim() ? { message: value.message } : {}),
      ...(isRecord(resource) &&
      resource.kind === CopilotResourceKindEnum.TRAINING_PLAN_SESSION &&
      typeof resource.title === "string" &&
      copilotResourceHref(resource)
        ? { resource: resource as unknown as CopilotResource }
        : {}),
    };
  }
  if (isDocumentAssistantError(value)) return value;
  return null;
}

function errorForStatus(status: number): ICopilotError {
  // 401 and 403 both mean "refused", but only one of them is fixed by signing in again.
  if (status === 401) {
    return {
      code: CopilotErrorCodeEnum.SESSION_EXPIRED,
      recovery: CopilotErrorRecoveryEnum.NONE,
    };
  }
  if (status === 403) {
    return {
      code: CopilotErrorCodeEnum.FORBIDDEN,
      recovery: CopilotErrorRecoveryEnum.NONE,
    };
  }
  if (status === 404) {
    return {
      code: CopilotErrorCodeEnum.NOT_FOUND,
      recovery: CopilotErrorRecoveryEnum.CORRECT_INPUT,
    };
  }
  if (status === 408 || status === 429 || status >= 500) {
    return {
      code: CopilotErrorCodeEnum.TEMPORARY_FAILURE,
      recovery: CopilotErrorRecoveryEnum.RETRY,
    };
  }

  return OPERATION_FAILED_ERROR;
}

export function normalizeCopilotError(value: unknown): ICopilotError {
  const structured = structuredErrorOf(value);
  if (structured) return structured;

  if (value instanceof CopilotHttpError) {
    return structuredErrorOf(value.payload) ?? errorForStatus(value.status);
  }
  if (value instanceof TypeError) {
    return {
      code: CopilotErrorCodeEnum.TEMPORARY_FAILURE,
      recovery: CopilotErrorRecoveryEnum.RETRY,
    };
  }

  return OPERATION_FAILED_ERROR;
}

/**
 * The error a broken round-trip shows. A failure nothing could name is the transport's own, and one
 * more attempt is what clears it — so it is offered, rather than reading as a dead end. Everything
 * the backend did name — a `data-error` payload, or an HTTP status — keeps the recovery it declared:
 * a refused right and an expired session are not worth retrying, and only the backend knows which.
 */
export function transportErrorOf(value: unknown): ICopilotError {
  const error = normalizeCopilotError(value);
  const isDeclared = !!structuredErrorOf(value) || value instanceof CopilotHttpError;

  if (isDeclared || error.code !== CopilotErrorCodeEnum.OPERATION_FAILED) return error;

  return {
    code: CopilotErrorCodeEnum.RESPONSE_INTERRUPTED,
    recovery: CopilotErrorRecoveryEnum.RETRY,
  };
}

export function copilotFileErrorOf(file: Pick<File, "size" | "type">): ICopilotError | null {
  if (file.type !== ACCEPTED_FILE_MEDIA_TYPE) {
    return {
      code: CopilotErrorCodeEnum.DOCUMENT_REQUIRED,
      recovery: CopilotErrorRecoveryEnum.ATTACH_PDF,
    };
  }
  if (file.size > MAX_FILE_BYTES) {
    return {
      code: CopilotErrorCodeEnum.DOCUMENT_TOO_LARGE,
      recovery: CopilotErrorRecoveryEnum.ATTACH_PDF,
    };
  }

  return null;
}

export function getCopilotErrorPresentation(error: ICopilotError) {
  if (error.code === CopilotErrorCodeEnum.MULTI_TIMESLOT_SESSION) {
    return { message: error.message || ERROR_MESSAGES[error.code], actionLabel: null };
  }
  let actionLabel: string | null = null;

  if (error.recovery === CopilotErrorRecoveryEnum.RETRY) actionLabel = "Réessayer";
  if (error.recovery === CopilotErrorRecoveryEnum.ATTACH_PDF) actionLabel = "Ajouter un PDF";
  // Only the URL import can be corrected: `onErrorRecovery` reopens the submitted link, which it
  // reads back from the import prompt. The not-found codes carry no link, so no button either.
  if (error.recovery === CopilotErrorRecoveryEnum.CORRECT_INPUT && !LINKLESS_CORRECTIONS.has(error.code)) {
    actionLabel = "Corriger le lien";
  }

  return {
    message: ERROR_MESSAGES[error.code] ?? ERROR_MESSAGES[CopilotErrorCodeEnum.OPERATION_FAILED],
    actionLabel,
  };
}

export function createCopilotRetryTracker() {
  const retryCounts = new Map<string, number>();

  function canRetry(originatingMessageId: string): boolean {
    return (retryCounts.get(originatingMessageId) ?? 0) < MAX_MANUAL_RETRIES;
  }

  function recordRetry(originatingMessageId: string): boolean {
    if (!canRetry(originatingMessageId)) return false;

    retryCounts.set(originatingMessageId, (retryCounts.get(originatingMessageId) ?? 0) + 1);

    return true;
  }

  function reset() {
    retryCounts.clear();
  }

  return { canRetry, recordRetry, reset };
}

export function originatingUserMessageOf<T extends { role: string }>(
  messages: readonly T[],
  fromIndex: number,
): T | null {
  for (let index = Math.min(fromIndex, messages.length - 1); index >= 0; index -= 1) {
    if (messages[index]?.role === CopilotRoleEnum.USER) return messages[index] ?? null;
  }

  return null;
}

export function buildProgramUrlPrompt(url: string): string {
  return `${PROGRAM_URL_PROMPT_PREFIX}${url}`;
}

export function programUrlFromPrompt(prompt: string): string | null {
  if (!prompt.startsWith(PROGRAM_URL_PROMPT_PREFIX)) return null;

  return prompt.slice(PROGRAM_URL_PROMPT_PREFIX.length) || null;
}
